import { useCallback } from 'react';
import { Grid2, TextField, Chip, Typography, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import useNotify from '../../hooks/useNotify';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import CommonButton from '../../components/atom/CommonButton';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import PageWithTitle from '../../components/atom/PageWithTitle';
import { getLanguages, changeLanguage } from '../../i18n';
import { useQueryClient } from '@tanstack/react-query';
import { getGetMeQueryKey, useGetMe, useResetTwoFactor, useSwitchVenue } from '@openapi/auth/auth';
import { useEditMe } from '@openapi/users/users';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';

function ProfilePage() {
    const { t } = useTranslation();
    const { user, setUser, setSwitchIntoVenue, setAccessToken } = useAuth();
    const { notifyError, notifySuccess } = useNotify();
    const queryClient = useQueryClient();
    const { showModal } = useModals();

    const {
        data: me,
        isLoading,
        refetch,
    } = useGetMe({
        query: {
            onError: notifyError,
        },
    });

    const editMeMutation = useEditMe();
    const switchVenueMutation = useSwitchVenue();
    const resetTwoFactorMutation = useResetTwoFactor();

    const onSubmit = async (values, { setSubmitting }) => {
        try {
            await editMeMutation.mutateAsync({
                data: {
                    name: values.name,
                    email: values.email,
                },
            });

            if (user) {
                setUser({
                    ...user,
                    name: values.name,
                    email: values.email,
                });
            } else if (me) {
                setUser({
                    ...me,
                    name: values.name,
                    email: values.email,
                });
            }

            await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
            await refetch();
            notifySuccess(t('users.notifications.profileUpdated'));
        } catch (err) {
            notifyError(err);
        } finally {
            setSubmitting(false);
        }
    };

    const switchVenue = useCallback(
        async (id) => {
            try {
                const switchVenueData = await switchVenueMutation.mutateAsync({
                    venueId: id,
                });

                const { token, ...otherUserData } = switchVenueData?.user || {};

                if (otherUserData && Object.keys(otherUserData).length > 0) {
                    setSwitchIntoVenue(otherUserData);
                }

                if (token) {
                    setAccessToken(token);
                }

                notifySuccess(t('users.notifications.switchVenue'));
                await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
                await refetch();
            } catch (error) {
                notifyError(error);
            }
        },
        [
            switchVenueMutation,
            setSwitchIntoVenue,
            setAccessToken,
            notifySuccess,
            notifyError,
            t,
            queryClient,
            refetch,
        ],
    );

    if (isLoading) {
        return true;
    }

    if (!me) {
        return null;
    }

    const validateSchema = Yup.object().shape({
        name: Yup.string().required(t('common.required')),
        // email: Yup.string().email(t('errors.email')).required(t('common.required')),
    });

    const languages = getLanguages();
    const initialValues = {
        name: me.name ?? '',
        email: me.email ?? '',
    };

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validateSchema}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue, touched, errors, dirty }) => (
                <Form>
                    <PageWithTitle title={t('users.myProfile')}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle
                                title={t('users.myDetails')}
                                paperStyle={{ height: '100%' }}
                            >
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label={t('users.columns.name')}
                                        autoComplete="off"
                                        name="name"
                                        value={values.name}
                                        onChange={(ev) => {
                                            setFieldValue('name', ev.target.value);
                                        }}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name ? errors.name : ''}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        autoComplete="off"
                                        label={t('users.columns.email')}
                                        value={values.email}
                                        disabled
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }} sx={{ pt: 2 }}>
                                    <CommonButton
                                        fullWidth
                                        disabled={
                                            isSubmitting || !dirty || editMeMutation.isPending
                                        }
                                        type="submit"
                                        label={t('common.save')}
                                        buttonType="primary"
                                    />
                                </Grid2>
                            </BlockWithTitle>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle
                                title={t('venues.title')}
                                paperStyle={{ height: '100%' }}
                            >
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography variant="body2" sx={{ textAlign: 'left' }}>
                                        {t('users.switchVenueDescription')}
                                    </Typography>
                                </Grid2>
                                {me?.venues?.map((e) => {
                                    return (
                                        <Chip
                                            key={e.id}
                                            sx={{ mr: 0.5, mt: 1.5, mb: 1.5 }}
                                            label={e.name}
                                            size="medium"
                                            color="primary"
                                            icon={
                                                user?.venueId === e.id ? (
                                                    <SwitchRightIcon />
                                                ) : (
                                                    <SwitchLeftIcon disabled />
                                                )
                                            }
                                            onClick={() => {
                                                switchVenue(e.id);
                                            }}
                                            disabled={
                                                user?.venueId === e.id ||
                                                switchVenueMutation.isPending
                                            }
                                        />
                                    );
                                })}
                            </BlockWithTitle>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle
                                title={t('users.security')}
                                paperStyle={{ height: '100%' }}
                            >
                                <Grid2 size={{ xs: 12 }}>
                                    {me?.isTwoFactorEnabled ? (
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                                showModal(MODAL_TYPE.CONFIRMATION, {
                                                    content: t('users.disableTwoFactorConfirm'),
                                                    confirm: async () => {
                                                        try {
                                                            await resetTwoFactorMutation.mutateAsync(
                                                                {
                                                                    data: {
                                                                        userId: me.id,
                                                                        venueId: me.venues?.[0]?.id,
                                                                    },
                                                                },
                                                            );
                                                            await queryClient.invalidateQueries({
                                                                queryKey: getGetMeQueryKey(),
                                                            });
                                                            await refetch();
                                                            notifySuccess(
                                                                t(
                                                                    'users.notifications.twoFactorDisabled',
                                                                ),
                                                            );
                                                        } catch (error) {
                                                            notifyError(error);
                                                        }
                                                    },
                                                });
                                            }}
                                        >
                                            {t('users.disableTwoFactor')}
                                        </Button>
                                    ) : (
                                        <CommonButton
                                            fullWidth
                                            type="button"
                                            label={t('users.enableTwoFactor')}
                                            onClick={() => {
                                                showModal(MODAL_TYPE.ENABLE_TWO_FACTOR, {
                                                    refetch,
                                                });
                                            }}
                                        />
                                    )}
                                </Grid2>
                            </BlockWithTitle>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle
                                title={t('common.language')}
                                paperStyle={{ height: '100%' }}
                            >
                                {languages.map((e) => {
                                    return (
                                        <Chip
                                            key={e}
                                            sx={{ mr: 0.5, mt: 1.5, mb: 1.5, minWidth: 56 }}
                                            label={e}
                                            size="medium"
                                            color="primary"
                                            onClick={() => {
                                                changeLanguage(e);
                                            }}
                                        />
                                    );
                                })}
                            </BlockWithTitle>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default ProfilePage;
